import '../style/ApiTestingInterview.css'

export default function ApiTestingGuide() {
  return (
    <div className="article-container">
      <h1>Comprehensive API Testing Interview Guide (100 Questions)</h1>
      <p className="article-intro">
        Master your next QA interview with this extensive list of API testing questions covering fundamentals to advanced architecture.
      </p>

      {/* --- SECTION 1: CORE FUNDAMENTALS --- */}
      <h2>Core Fundamentals</h2>
      
      <h3>1. What is API Testing?</h3>
      <p>API Testing is a type of software testing that verifies the functionality, reliability, performance, and security of APIs.</p>

      <h3>2. What are the common HTTP methods?</h3>
      <ul>
        <li>GET – Retrieve data</li>
        <li>POST – Create data</li>
        <li>PUT – Update/Replace data</li>
        <li>DELETE – Remove data</li>
        <li>PATCH – Partial update</li>
      </ul>

      <h3>3. What is the difference between API and Unit Testing?</h3>
      <p>Unit testing focuses on individual functions/classes by developers; API testing focuses on the interface and business logic by QA.</p>

      <h3>4. What is REST?</h3>
      <p>Representational State Transfer (REST) is an architectural style that uses HTTP for communication and is stateless.</p>

      <h3>5. What is SOAP?</h3>
      <p>Simple Object Access Protocol (SOAP) is a protocol that uses XML for messaging and has strict standards.</p>

      <h3>6. What is an API Endpoint?</h3>
      <p>An endpoint is the specific URL (e.g., /api/v1/users) where an API receives requests.</p>

      <h3>7. What is JSON?</h3>
      <p>JavaScript Object Notation (JSON) is a lightweight, text-based data format easy for humans to read and machines to parse.</p>

      <h3>8. What are the main components of an HTTP Request?</h3>
      <ul>
        <li>Verb (GET, POST, etc.)</li>
        <li>URI (Endpoint)</li>
        <li>Headers (Metadata)</li>
        <li>Request Body (Payload)</li>
      </ul>

      <h3>9. What are the main components of an HTTP Response?</h3>
      <ul>
        <li>Status Code (e.g., 200 OK)</li>
        <li>Response Headers</li>
        <li>Response Body</li>
      </ul>

      <h3>10. What is a URI?</h3>
      <p>Uniform Resource Identifier (URI) is a string of characters used to identify a resource.</p>

      {/* --- SECTION 2: STATUS CODES --- */}
      <h2>HTTP Status Codes</h2>
      

      <h3>11. What does a 201 Status Code mean?</h3>
      <p>201 Created: The request was successful and a new resource was created.</p>

      <h3>12. What does a 204 Status Code mean?</h3>
      <p>204 No Content: The request was successful, but there is no data to return (common for DELETE).</p>

      <h3>13. Explain 301 vs 302.</h3>
      <p>301 is a Permanent Redirect; 302 is a Temporary Redirect.</p>

      <h3>14. What is a 400 Bad Request?</h3>
      <p>The server cannot process the request due to client-side errors (e.g., malformed syntax).</p>

      <h3>15. What is 401 Unauthorized?</h3>
      <p>The request requires user authentication (invalid or missing credentials).</p>

      <h3>16. What is 403 Forbidden?</h3>
      <p>The server understands the request but refuses to authorize it (authenticated but no permission).</p>

      <h3>17. What is 404 Not Found?</h3>
      <p>The server cannot find the requested resource.</p>

      <h3>18. What is 405 Method Not Allowed?</h3>
      <p>The HTTP method used is not supported for that specific resource.</p>

      <h3>19. What is 500 Internal Server Error?</h3>
      <p>A generic error message when the server encounters an unexpected condition.</p>

      <h3>20. What is 503 Service Unavailable?</h3>
      <p>The server is currently unable to handle the request (overloaded or down for maintenance).</p>

      {/* --- SECTION 3: ADVANCED CONCEPTS --- */}
      <h2>Advanced Concepts</h2>

      <h3>21. What is Payload in API?</h3>
      <p>The actual data sent in the HTTP request body (usually in JSON or XML format).</p>

      <h3>22. What is Idempotency?</h3>
      <p>An operation is idempotent if performing it multiple times has the same effect as performing it once (e.g., GET, PUT, DELETE).</p>

      <h3>23. What is API Latency?</h3>
      <p>The time it takes for a request to travel from the client to the server and back.</p>

      <h3>24. What is Throughput?</h3>
      <p>The number of API requests a system can handle in a specific time frame.</p>

      <h3>25. What is Rate Limiting?</h3>
      <p>A technique to control the rate of incoming requests to prevent server abuse.</p>

      <h3>26. What is API Throttling?</h3>
      <p>The process of slowing down or rejecting requests when a user exceeds their limit.</p>

      <h3>27. What is Content-Type Header?</h3>
      <p>It tells the client/server the media type of the resource (e.g., application/json).</p>

      <h3>28. What is a Bearer Token?</h3>
      <p>A security token given to the client by an authentication server, used in the Authorization header.</p>

      <h3>29. What is OAuth 2.0?</h3>
      <p>An industry-standard protocol for authorization that allows third-party apps to access user data without sharing passwords.</p>

      <h3>30. What is API Documentation?</h3>
      <p>A technical manual containing instructions on how to effectively use and integrate with an API (e.g., Swagger/OpenAPI).</p>

      {/* --- CONTINUING TO 100... --- */}
      {/* (Adding categories for briefness in this display, but you can expand these into <h3> tags) */}
      
      <h2>Security & Tools (Questions 31-60)</h2>
      <h3>31. Difference between Authentication and Authorization?</h3>
      <p>AuthN is "Who are you?"; AuthZ is "What are you allowed to do?".</p>
      <h3>32. What is Postman?</h3>
      <p>A popular GUI-based tool for testing APIs.</p>
      <h3>33. What are Postman Collections?</h3>
      <p>A group of saved API requests that can be organized into folders.</p>
      <h3>34. What is Newman?</h3>
      <p>The command-line runner for Postman collections.</p>
      <h3>35. What is Rest Assured?</h3>
      <p>A Java library used for automating RESTful web services.</p>
      <h3>36. What is Swagger?</h3>
      <p>A toolset for designing, building, and documenting RESTful APIs.</p>
      <h3>37. What is an API Gateway?</h3>
      <p>A server that acts as an entry point for all API calls, handling routing and security.</p>
      <h3>38. What is SQL Injection in API?</h3>
      <p>A security vulnerability where an attacker sends malicious SQL queries via API inputs.</p>
      <h3>39. What is Cross-Origin Resource Sharing (CORS)?</h3>
      <p>A security feature that restricts web pages from making requests to a different domain.</p>
      <h3>40. What is a JWT (JSON Web Token)?</h3>
      <p>A compact, URL-safe means of representing claims to be transferred between two parties.</p>
<h3>41. What is API Key Authentication?</h3>
<p>API Key authentication uses a unique key passed in headers or query parameters to identify the client making the request.</p>

<h3>42. What is Basic Authentication?</h3>
<p>Basic authentication sends username and password encoded in Base64 in the Authorization header.</p>

<h3>43. What is Bearer Authentication?</h3>
<p>Bearer authentication uses a token (usually JWT) in the Authorization header to access protected APIs.</p>

<h3>44. What is SSL/TLS?</h3>
<p>SSL/TLS encrypts data transmitted between client and server to ensure secure communication.</p>

<h3>45. What is HTTPS?</h3>
<p>HTTPS is the secure version of HTTP that uses SSL/TLS encryption.</p>

<h3>46. What is API Security Testing?</h3>
<p>API security testing ensures that APIs are protected from threats like unauthorized access, injection attacks, and data leaks.</p>

<h3>47. What is Postman Environment?</h3>
<p>Postman environments allow you to store variables like URLs, tokens, and credentials for different environments.</p>

<h3>48. What is Pre-request Script in Postman?</h3>
<p>A script executed before the API request is sent, often used to generate tokens or dynamic data.</p>

<h3>49. What is Test Script in Postman?</h3>
<p>Test scripts validate API responses such as status codes, response body, and response time.</p>

<h3>50. What is API Automation?</h3>
<p>API automation is the process of writing scripts to automatically test APIs without manual intervention.</p>

<h3>51. What is Rest Assured?</h3>
<p>Rest Assured is a Java library used to automate REST API testing.</p>

<h3>52. What is SoapUI?</h3>
<p>SoapUI is a tool used to test SOAP and REST APIs.</p>

<h3>53. What is JMeter?</h3>
<p>Apache JMeter is a tool used for performance and load testing of APIs.</p>

<h3>54. What is Newman?</h3>
<p>Newman is the command-line collection runner for Postman.</p>

<h3>55. What is API Mock Server?</h3>
<p>A mock server simulates API responses for testing when the real API is not available.</p>

<h3>56. What is Request Chaining?</h3>
<p>Request chaining is passing data from one API response to another request.</p>

<h3>57. What is Data Driven API Testing?</h3>
<p>Testing APIs using multiple data sets stored in external files like CSV or JSON.</p>

<h3>58. What is Schema Validation?</h3>
<p>Schema validation checks whether the API response structure matches the expected schema.</p>

<h3>59. What is Negative Testing in API?</h3>
<p>Negative testing checks how APIs behave with invalid inputs or unexpected data.</p>

<h3>60. What is API Regression Testing?</h3>
<p>Regression testing ensures that new changes do not break existing API functionality.</p>
      
      <h2>Operational & Performance (Questions 61-100)</h2>
      <h3>61. What is Contract Testing?</h3>
      <p>Ensuring that a provider (API) and a consumer (App) agree on the format of the data exchange.</p>
      <h3>62. What is API Mocking?</h3>
      <p>Creating a fake version of an API to test the front-end before the back-end is ready.</p>
      <h3>63. What is Regression Testing in API?</h3>
      <p>Testing existing API endpoints after changes to ensure nothing broke.</p>
      <h3>64. What is Schema Validation?</h3>
      <p>Checking if the JSON response matches the predefined structure (data types, required fields).</p>
      <h3>65. What is Fuzz Testing?</h3>
      <p>Sending random or invalid data to an API to see if it crashes.</p>
      <h3>66. Can you use GET instead of PUT?</h3>
      <p>Technically yes, but it violates REST principles and is poor practice.</p>
      <h3>67. What is a Query Parameter?</h3>
      <p>Key-value pairs added to the end of a URL (e.g., ?id=123) to filter results.</p>
      <h3>68. What is a Path Parameter?</h3>
      <p>Variables that are part of the URL path (e.g., /users/).</p>
      <h3>69. What is Statelessness?</h3>
      <p>Every request from a client contains all the information needed to understand the request.</p>
      <h3>70. What is a Webhook?</h3>
      <p>A "reverse API" where the server pushes data to the client when an event happens.</p>
      <h3>71. What is API Versioning?</h3>
<p>API versioning allows developers to introduce changes without breaking existing clients (e.g., /api/v1/users).</p>

<h3>72. What are Headers in API?</h3>
<p>Headers contain metadata such as Content-Type, Authorization, and Accept.</p>

<h3>73. What is Content Negotiation?</h3>
<p>The process where the client and server decide the format of the response (JSON, XML).</p>

<h3>74. What is GraphQL?</h3>
<p>GraphQL is a query language for APIs that allows clients to request only the data they need.</p>

<h3>75. What is gRPC?</h3>
<p>gRPC is a high-performance RPC framework that uses HTTP/2 and Protocol Buffers.</p>

<h3>76. What is API Load Testing?</h3>
<p>Testing how an API behaves under heavy user traffic.</p>

<h3>77. What is Stress Testing in API?</h3>
<p>Testing the API beyond its limits to see how it fails.</p>

<h3>78. What is Spike Testing?</h3>
<p>Testing sudden spikes in API traffic.</p>

<h3>79. What is API Monitoring?</h3>
<p>Tracking API performance, uptime, and response times continuously.</p>

<h3>80. What is Circuit Breaker Pattern?</h3>
<p>A design pattern that prevents cascading failures in microservices.</p>

<h3>81. What is API Pagination?</h3>
<p>Pagination divides large datasets into smaller pages (e.g., page=1&limit=10).</p>

<h3>82. What is Sorting in APIs?</h3>
<p>Sorting allows API responses to be ordered by fields like name or date.</p>

<h3>83. What is Filtering?</h3>
<p>Filtering allows clients to retrieve specific data using parameters.</p>

<h3>84. What is HATEOAS?</h3>
<p>Hypermedia As The Engine Of Application State provides links within API responses.</p>

<h3>85. What is API Caching?</h3>
<p>Caching stores responses temporarily to reduce server load.</p>

<h3>86. What is ETag?</h3>
<p>An HTTP header used for caching and validating resources.</p>

<h3>87. What is Rate Limit Header?</h3>
<p>Headers that inform the client about request limits.</p>

<h3>88. What is API Gateway Rate Limiting?</h3>
<p>Limiting requests at the gateway to protect backend services.</p>

<h3>89. What is Microservices Architecture?</h3>
<p>An architecture where applications are built as independent services.</p>

<h3>90. What is Service Discovery?</h3>
<p>A method for services to find each other dynamically.</p>

<h3>91. What is API Contract?</h3>
<p>A formal agreement defining request and response structure.</p>

<h3>92. What is OpenAPI Specification?</h3>
<p>A standard format used to describe REST APIs.</p>

<h3>93. What is API Lifecycle?</h3>
<p>The stages of API development including design, development, testing, deployment, and monitoring.</p>

<h3>94. What is Canary Deployment?</h3>
<p>A deployment strategy where new versions are released to a small group of users first.</p>

<h3>95. What is Blue-Green Deployment?</h3>
<p>A deployment strategy using two identical environments to reduce downtime.</p>

<h3>96. What is API Logging?</h3>
<p>Recording API requests and responses for debugging and monitoring.</p>

<h3>97. What is Distributed Tracing?</h3>
<p>Tracking a request across multiple microservices.</p>

<h3>98. What is API SLA?</h3>
<p>Service Level Agreement defining uptime and performance guarantees.</p>

<h3>99. What is API Governance?</h3>
<p>Policies and standards used to manage APIs in an organization.</p>

<h3>100. What is the best practice for API Testing?</h3>
<ul>
<li>Validate status codes</li>
<li>Validate response body</li>
<li>Check response time</li>
<li>Test edge cases</li>
<li>Automate regression tests</li>
</ul>

    </div>
  );
}